import React, { useContext, useEffect, useState } from 'react';
import { Link } from '@uhg-abyss/web/ui/Link';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { DEFAULT_REPORT_TYPE } from '@src/components/Reports/Reports';
import { Table } from '@src/common/Table';
import { useLazyGetAllParentFilesQuery as cdbFileQuery } from '@src/store/services/cdb/onlineServices';
import { useLazyGetGPSAllParentFilesQuery as gpsFileQuery } from '@src/store/services/gps/onlineServices';
import { FileContext } from '@src/routes/Routes';
import { useAuth } from '@src/Providers/AuthProvider';
import { statusListArr } from './data';
import ChildTable from './ChildTable';
import { Box } from '@uhg-abyss/web/ui/Box';
import { encryptProfileParams } from '@src/utils';

export const FilesListTable = () => {
  const router = useRouter();
  const { service: routeService } = router.getRouteParams();
  const { setFileData } = useContext(FileContext);
  const { user } = useAuth();
  const [favourites, setFavourites] = useState(
    user?.userType === 'I' ? !!user?.userFavourites : false
  );
  const isExtType = user?.userType === 'E';
  const service = routeService ?? user?.defaultAccess;

  useEffect(() => {
    setFavourites(user?.userType === 'I' ? !!user?.userFavourites : false);
  }, [user?.userId]);

  const columns = React.useMemo(() => {
    return [
      {
        Header: 'File ID',
        accessor: 'fileID',
        minWidth: 90,
        width: 90,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{value}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Customer Name',
        accessor: 'customerName',
        minWidth: 250,
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ value, row }) => {
          if (!user?.viewProfile) return <strong>{value}</strong>;
          return (
            <Link
              href={`/profile/${encryptProfileParams(
                row?.original?.prflverNbr?.trim(),
                row?.original?.asgnSbmtId?.trim(),
                row?.original?.eligSysCode?.trim()
              )}`}
            >
              <Box color={''}>
                <strong>{value}</strong>
              </Box>
            </Link>
          );
        },
      },
      {
        Header: 'Received',
        minWidth: 220,
        accessor: 'fileRecvDate',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          const formattedDate = `${value?.split(' ')[0]} ${value
            ?.split(' ')[1]
            ?.substring(0, 5)}`;
          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{formattedDate}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Status',
        accessor: 'fileStatus',
        minWidth: 250,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          if (row?.original?.setIdCount > 1) {
            return '';
          }
          const formattedValue = value?.replace(/_/g, ' ') || '';
          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{formattedValue}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'File Name',
        accessor: 'fileName',
        minWidth: 250,
        canToggleVisibilty: true,
        isHiddenByDefault: isExtType,
        Cell: ({ value }) => {
          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{value}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Options',
        accessor: '',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        label: 'Reports',
        Cell: ({ row }) => {
          const isStatus = statusListArr.includes(
            row?.original?.fileStatus?.trim()
          );

          const handleClick = () => {
            return setFileData((prevData) => {
              return { ...prevData, ...row.original };
            });
          };

          if (row?.original?.setIdCount > 1) {
            return '';
          }

          return (
            <Flex
              css={{
                gap: '10px',
                padding: '0 10px',
              }}
            >
              {isStatus ? (
                <strong>Rpt</strong>
              ) : row?.original?.locTypCd?.trim() === '1' &&
                user?.locType !== 1 ? (
                <strong>Rpt</strong>
              ) : (
                <Link
                  onClick={handleClick}
                  href={`/home/reports/${row?.original?.transSetId}/${DEFAULT_REPORT_TYPE}`}
                >
                  Rpt
                </Link>
              )}
              {!isExtType &&
                (user?.viewMntr ? (
                  <Link
                    href={`/home/monitor/${row?.original?.transSetId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Mntr
                  </Link>
                ) : (
                  <strong>Mntr</strong>
                ))}
            </Flex>
          );
        },
      },
    ];
  }, [setFileData, user]);

  const gpsColumns = React.useMemo(() => {
    return [
      {
        Header: 'File ID',
        accessor: 'fileID',
        minWidth: 70,
        width: 70,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{value}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Customer Name',
        accessor: 'customerName',
        minWidth: 220,
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ value, row }) => {
          if (!user?.viewProfile)
            return (
              <Box color={''}>
                <strong>{value}</strong>
              </Box>
            );
          return (
            <Link
              href={`/profile/${encryptProfileParams(
                row?.original?.prflVerNbr?.trim(),
                row?.original?.asgnSbmtId?.trim(),
                row?.original?.eligSysCd?.trim()
              )}`}
            >
              <Box color={''}>
                <strong>{value}</strong>
              </Box>
            </Link>
          );
        },
      },
      {
        Header: 'Received',
        accessor: 'fileRecvDate',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          // Extract the date and time parts
          const datePart = value.slice(0, 10); // "2024-11-11"
          const timePart = value.slice(11, 16); // "00.01"

          // Combine them with a space in between
          const formattedValue = `${datePart} ${timePart}`;

          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{formattedValue}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Status',
        accessor: 'fileStatus',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 220,
        Cell: ({ value }) => {
          const statusLabel =
            value?.replace(/_/g, ' ')?.charAt(0)?.toUpperCase() +
            value?.replace(/_/g, ' ')?.slice(1);
          return <Badge variant={value}>{statusLabel}</Badge>;
        },
      },
      {
        Header: 'File Name',
        accessor: 'fileName',
        minWidth: 300,
        canToggleVisibilty: true,
        isHiddenByDefault: isExtType,
        Cell: ({ value }) => {
          return (
            <Box color={''}>
              <strong style={{ fontSize: '14px' }}>{value}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Options',
        accessor: '',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        label: 'Reports',
        Cell: ({ row }) => {
          const isStatus = statusListArr.includes(
            row?.original?.fileStatus?.trim()
          );

          const handleClick = () => {
            return setFileData((prevData) => {
              return { ...prevData, ...row.original };
            });
          };

          return (
            <Flex
              css={{
                gap: '10px',
                padding: '0 10px',
              }}
            >
              {isStatus ? (
                <strong>Rpt</strong>
              ) : (
                <React.Fragment>
                  <Link
                    onClick={handleClick}
                    href={`/gps/home/reports/${row.original.transSetId}/${DEFAULT_REPORT_TYPE}`}
                  >
                    Rpt
                  </Link>
                </React.Fragment>
              )}
              {!isExtType &&
                (user?.viewMntr ? (
                  <Link
                    href={`/home/monitor/${row?.original?.transSetId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Mntr
                  </Link>
                ) : (
                  <strong>Mntr</strong>
                ))}
            </Flex>
          );
        },
      },
    ];
  }, [setFileData, user]);

  return (
    <Card
      css={{
        'table td': {
          overflowY: 'hidden',
        },
      }}
    >
      <div>
        <React.Fragment>
          <Table
            columns={service === 'gps' ? gpsColumns : columns}
            dataKey={`fileListTable${service}`}
            fetch={service === 'gps' ? gpsFileQuery : cdbFileQuery}
            requestParams={{
              favourites,
              user,
            }}
            customHeaderSection={
              <div style={{ padding: '10px' }}>
                {user?.userType === 'I' ? (
                  <SelectInput
                    label="Files list filter"
                    hideLabel
                    placeholder="Pick one"
                    value={String(favourites)}
                    width={'150px'}
                    onChange={(favState) => {
                      setFavourites(favState === 'true');
                    }}
                    options={[
                      ...(user?.userFavourites
                        ? [{ value: 'true', label: 'My Favorites List' }]
                        : []),
                      { value: 'false', label: 'Recent Files' },
                    ]}
                    customRender={(option) => (
                      <span style={{ fontSize: '14px' }}>{option.label}</span>
                    )}
                    css={{
                      'abyss-select-input-container': {
                        fontSize: '14px !important',
                      },
                    }}
                  />
                ) : null}
              </div>
            }
            renderSubComponent={(row) => {
              return <ChildTable row={row} />;
            }}
            pageSizeDefault={25}
            tableCss={{
              'abyss-data-table-header-container': {
                overflow: 'hidden !important',
              },
              'abyss-data-table-header-data': {
                fontSize: '14px',
              },
              'abyss-data-table-cell , .abyss-data-table-expand-container:disabled':
                {
                  display: 'none',
                },
            }}
            downloadMenuItems={[
              {
                title: 'Download CSV',
                onClick: 'exportVisibleData',
                csvFilename: `files-list-${service}.csv`,
              },
            ]}
            showGlobalFilter={!isExtType}
          />
        </React.Fragment>
      </div>
    </Card>
  );
};
